import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listPublicVideos } from '@/api/contentService';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, X, Film, ExternalLink } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { isNativePlatform, openExternalUrl } from '@/utils/nativeAndroidFiles';

export default function Videos() {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [filter, setFilter] = useState('all');
  const nativeApp = isNativePlatform();

  const { data: videos = [], isLoading } = useQuery({
    queryKey: ['videos'],
    queryFn: () => listPublicVideos(50)
  });

  const getYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url?.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const getYouTubeThumbnail = (url) => {
    const videoId = getYouTubeId(url);
    return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;
  };

  const categories = ['all', ...new Set(videos.map(v => v.category).filter(Boolean))];
  
  const filteredVideos = filter === 'all' 
    ? videos 
    : videos.filter(v => v.category === filter);

  const categoryColors = {
    'Pregação': 'bg-indigo-100 text-indigo-700',
    'Louvor': 'bg-purple-100 text-purple-700',
    'Testemunho': 'bg-emerald-100 text-emerald-700',
    'Estudo': 'bg-amber-100 text-amber-700',
    'Evento': 'bg-rose-100 text-rose-700',
    'Outro': 'bg-gray-100 text-gray-700',
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="max-w-lg mx-auto px-4 pt-4 pb-6">
        <PageHeader title="Vídeos" subtitle="Mensagens e pregações" showBack={true} className="mb-4" />

        {/* Category Filter */}
        {categories.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-4 scrollbar-hide mb-4">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  filter === cat
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                {cat === 'all' ? 'Todos' : cat}
              </button>
            ))}
          </div>
        )}

        {filteredVideos.length === 0 ? (
          <EmptyState
            icon={Film}
            title="Nenhum vídeo disponível"
            description="Em breve teremos vídeos para você assistir."
          />
        ) : (
          <div className="space-y-4">
            {filteredVideos.map((video, index) => {
              const thumbnail = video.thumbnail_url || getYouTubeThumbnail(video.youtube_url);
              
              return (
                <motion.div
                  key={video.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-2xl shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => {
                    if (nativeApp) {
                      openExternalUrl(video.youtube_url, { sameWindow: true });
                      return;
                    }
                    setSelectedVideo(video);
                  }}
                >
                  <div className="relative aspect-video">
                    {thumbnail ? (
                      <img
                        src={thumbnail}
                        alt={video.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        <Play className="w-12 h-12 text-gray-400" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
                        <Play className="w-8 h-8 text-indigo-600 ml-1" />
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-semibold text-gray-900 line-clamp-2">{video.title}</h3>
                      {video.category && (
                        <Badge className={categoryColors[video.category] || categoryColors['Outro']}>
                          {video.category}
                        </Badge>
                      )}
                    </div>
                    {video.description && (
                      <p className="text-sm text-gray-500 mt-2 line-clamp-2">{video.description}</p>
                    )}
                    {nativeApp && (
                      <div className="mt-3 flex items-center gap-2 text-sm font-medium text-indigo-600">
                        <ExternalLink className="w-4 h-4" />
                        Abrir vídeo
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Video Player Modal */}
      <AnimatePresence>
        {!nativeApp && selectedVideo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black z-50 flex flex-col"
          >
            <div className="flex items-center justify-between p-4 text-white">
              <h2 className="font-semibold truncate flex-1 mr-4">{selectedVideo.title}</h2>
              <Button variant="ghost" size="icon" onClick={() => setSelectedVideo(null)}>
                <X className="w-6 h-6" />
              </Button>
            </div>

            <div className="flex-1 flex items-center justify-center">
              <iframe
                src={`https://www.youtube.com/embed/${getYouTubeId(selectedVideo.youtube_url)}?autoplay=1`}
                title={selectedVideo.title}
                className="w-full aspect-video max-h-[80vh]"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
